import { useEffect, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Check, Search } from 'lucide-react'
import apiClient from '@/api/client'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'

const MAX = 6

/** Pick up to six dishes to feature; the first with a photo leads the posts. */
export function DishPicker({
  open,
  onOpenChange,
  selected,
  onSave,
}: {
  open: boolean
  onOpenChange: (o: boolean) => void
  selected: string[]
  onSave: (ids: string[]) => void
}) {
  const [picked, setPicked] = useState<string[]>(selected)
  const [q, setQ] = useState('')
  useEffect(() => {
    if (open) {
      setPicked(selected)
      setQ('')
    }
  }, [open, selected])
  const query = useQuery({
    queryKey: ['marketing', 'kit-dishes'],
    queryFn: () => apiClient.getAdminProducts({ per_page: 500 }),
    enabled: open,
    staleTime: 5 * 60_000,
  })
  const products = useMemo(() => {
    const all = query.data?.data ?? []
    const needle = q.trim().toLowerCase()
    return needle ? all.filter((p) => p.name.toLowerCase().includes(needle)) : all
  }, [query.data, q])

  const toggle = (id: string) =>
    setPicked((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : cur.length >= MAX ? cur : [...cur, id]))

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Featured dishes</DialogTitle>
          <DialogDescription>Pick up to {MAX}. Dishes with a photo make the best posts.</DialogDescription>
        </DialogHeader>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input className="pl-9" placeholder="Search the menu" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        <div className="max-h-80 space-y-1 overflow-y-auto">
          {query.isLoading ? (
            <p className="p-2 text-sm text-muted-foreground">Loading the menu…</p>
          ) : products.length === 0 ? (
            <p className="p-2 text-sm text-muted-foreground">No dishes match.</p>
          ) : (
            products.map((p) => {
              const on = picked.includes(p.id)
              return (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => toggle(p.id)}
                  className={cn('flex w-full items-center gap-3 rounded-md p-2 text-left hover:bg-muted/50', on && 'bg-primary/5')}
                >
                  {p.image_url ? (
                    <img src={p.image_url} alt="" className="h-9 w-9 rounded object-cover" />
                  ) : (
                    <div className="h-9 w-9 rounded bg-muted" />
                  )}
                  <span className="min-w-0 flex-1 truncate text-sm">{p.name}</span>
                  {on && <Check className="h-4 w-4 text-primary" />}
                </button>
              )
            })
          )}
        </div>
        <DialogFooter>
          <span className="mr-auto self-center text-xs text-muted-foreground">
            {picked.length} of {MAX}
          </span>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={() => onSave(picked)}>Save dishes</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
